import React from 'react'
import clsx from 'clsx'
import { User } from 'lucide-react'

type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl'

const getSizeClasses = (size: AvatarSize) => {
  const sizes = {
    xs: 'h-6 w-6 text-xs',
    sm: 'h-8 w-8 text-sm',
    md: 'h-10 w-10 text-base',
    lg: 'h-12 w-12 text-lg',
    xl: 'h-16 w-16 text-xl',
    '2xl': 'h-20 w-20 text-2xl',
  }
  return sizes[size]
}

const getIndicatorClasses = (size: AvatarSize) => {
  const indicators = {
    xs: 'h-1.5 w-1.5 border',
    sm: 'h-2 w-2 border',
    md: 'h-2.5 w-2.5 border-2',
    lg: 'h-3 w-3 border-2',
    xl: 'h-4 w-4 border-2',
    '2xl': 'h-5 w-5 border-2',
  }
  return indicators[size]
}

export interface AvatarProps {
  src?: string
  alt?: string
  fallback?: string
  size?: AvatarSize
  className?: string
  online?: boolean
  onlineIndicatorClassName?: string
  onClick?: () => void
}

const Avatar: React.FC<AvatarProps> = ({
  src,
  alt,
  fallback,
  size = 'md',
  className,
  online,
  onlineIndicatorClassName,
  onClick,
}) => {
  const [imageError, setImageError] = React.useState(false)
  const [imageLoaded, setImageLoaded] = React.useState(false)

  const handleImageError = () => {
    setImageError(true)
  }

  const handleImageLoad = () => {
    setImageLoaded(true)
  }

  const getInitials = (name?: string) => {
    if (!name) return 'U'
    
    const words = name.trim().split(' ')
    if (words.length === 1) {
      return words[0].charAt(0).toUpperCase()
    }
    return `${words[0].charAt(0)}${words[words.length - 1].charAt(0)}`.toUpperCase()
  }

  const showImage = src && !imageError
  const showFallback = !showImage

  return (
    <div className={clsx('relative inline-block', onClick && 'cursor-pointer')} onClick={onClick}>
      <div
        className={clsx(
          'flex items-center justify-center rounded-full bg-dark-500 border border-dark-400 text-text-primary font-medium overflow-hidden transition-all duration-200',
          getSizeClasses(size),
          onClick && 'hover:scale-105 hover:border-primary-500',
          className
        )}
      >
        {showImage && (
          <>
            <img
              src={src}
              alt={alt || 'Avatar'}
              className={clsx(
                'h-full w-full object-cover transition-opacity duration-200',
                imageLoaded ? 'opacity-100' : 'opacity-0'
              )}
              onError={handleImageError}
              onLoad={handleImageLoad}
            />
            {!imageLoaded && (
              <div className="absolute inset-0 bg-dark-500 animate-pulse" />
            )}
          </>
        )}
        
        {showFallback && (
          <>
            {fallback ? (
              <span className="font-semibold select-none">
                {getInitials(fallback)}
              </span>
            ) : (
              <User className={clsx(
                'text-text-muted',
                size === 'xs' || size === 'sm' ? 'h-3 w-3' : 
                size === 'md' ? 'h-4 w-4' : 
                size === 'lg' ? 'h-5 w-5' :
                size === 'xl' ? 'h-6 w-6' : 'h-8 w-8'
              )} />
            )}
          </>
        )}
      </div>
      
      {/* Online indicator */}
      {online !== undefined && (
        <div
          className={clsx(
            'absolute rounded-full border-dark-600 bottom-0 right-0 transform translate-x-1 translate-y-1',
            getIndicatorClasses(size),
            online ? 'bg-success-500' : 'bg-dark-400',
            onlineIndicatorClassName
          )}
        />
      )}
    </div>
  )
}

// Avatar Group Component
export interface AvatarGroupProps {
  avatars: Array<{
    id: string
    src?: string
    alt?: string
    fallback?: string
    online?: boolean
  }>
  size?: AvatarSize
  max?: number
  className?: string
  onAvatarClick?: (id: string) => void
}

const AvatarGroup: React.FC<AvatarGroupProps> = ({
  avatars,
  size = 'md',
  max = 5,
  className,
  onAvatarClick,
}) => {
  const displayedAvatars = avatars.slice(0, max)
  const remainingCount = avatars.length - max

  return (
    <div className={clsx('flex -space-x-2', className)}>
      {displayedAvatars.map((avatar, index) => (
        <div
          key={avatar.id}
          className="relative ring-2 ring-dark-600 rounded-full"
          style={{ zIndex: displayedAvatars.length - index }}
        >
          <Avatar
            {...avatar}
            size={size}
            onClick={() => onAvatarClick?.(avatar.id)}
          />
        </div>
      ))}
      
      {remainingCount > 0 && (
        <div
          className={clsx(
            'flex items-center justify-center rounded-full bg-dark-400 text-text-secondary text-xs font-medium ring-2 ring-dark-600',
            getSizeClasses(size)
          )}
        >
          +{remainingCount}
        </div>
      )}
    </div>
  )
}

export { Avatar, AvatarGroup }